import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box, Skeleton, Tabs } from '@mui/material';
import Avatar from '@sharedCR/common/Avatar';
import UsersAvatarGroup from '@sharedCR/common/UsersAvatarGroup';
import DataDisplay, {
  useResponsiveDataDisplayVariant,
} from '@sharedCR/coreDesignSystem/DataDisplay';
import { WithLoadingPropTypes } from 'hocs/withLoading';

import useModifiedFieldPendingChangeAuthors from 'hooks/useModifiedFieldPendingChangeAuthors';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { getPersonInitials } from 'utils/loanApplication';

import {
  selectFinancialInfoBorrowers,
  selectFinancialInfoCashToClose,
  selectFinancialInfoCurrentFinancialsBorrowerAssets,
  selectFinancialInfoCurrentFinancialsBorrowerIncomes,
  selectFinancialInfoCurrentFinancialsBorrowerLiabilities,
  selectFinancialInfoCurrentFinancialsBorrowerNegativeRentalIncome,
  selectFinancialInfoDebtToIncome,
  selectFinancialInfoSummaryPendingFields,
  selectFinancialInfoPendingChanges,
  selectFinancialInfoProjectedReserves,
  selectFinancialInfoTotalAssets,
  selectFinancialInfoTotalIncomes,
  selectFinancialInfoTotalLiabilities,
  selectFinancialInfoTotalNegativeRentalIncome,
} from 'store/loanApplications/selectors';

import {
  ALL_BORROWERS_LABEL,
  BORROWER_FULL_NAME_FIELD,
  ALL_BORROWERS_PATH,
  FINANCIAL_INFO_TABLE_KEY,
  FINANCIAL_INFO_VIEWS_PATHS,
} from '../FinancialInfo.constants';
import { generateFinancialInfoPath } from '../FinancialInfo.utils';

import { getSummaryInfoConfig } from './FinancialInfoHead.config';
import {
  getSummaryWrapperStyles,
  scrollButtonStyles,
  summaryCardStyles,
  tabsSkeletonsWrapperStyles,
  tabsWrapperStyles,
  wrapperStyles,
} from './FinancialInfoHead.styles';
import NavigationTab from './NavigationTab';

const tabScrollButtonProps = {
  disableRipple: true,
  sx: scrollButtonStyles,
};

const TABS_SKELETONS_LENGTH = 3;
const SUMMARY_SKELETONS_LENGTH = 5;

const FinancialInfoHead = ({ isLoading }) => {
  const history = useHistory();
  const {
    applicationId,
    tab,
    subtab: tablePath,
    entityId: borrowerId,
    section: actionType,
    subsection: recordId,
  } = useParams();

  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();

  const dataDisplayVariant = useResponsiveDataDisplayVariant();

  const borrowers = useSelector(selectFinancialInfoBorrowers);
  const cashToClose = useSelector(selectFinancialInfoCashToClose);
  const projectedReserves = useSelector(selectFinancialInfoProjectedReserves);
  const summaryPendingFields = useSelector(selectFinancialInfoSummaryPendingFields);
  const dti = useSelector(selectFinancialInfoDebtToIncome);
  const savedIncomes = useSelector(selectFinancialInfoCurrentFinancialsBorrowerIncomes(borrowerId));
  const totalIncomes = useSelector(selectFinancialInfoTotalIncomes({ borrowerId }));
  const savedAssets = useSelector(selectFinancialInfoCurrentFinancialsBorrowerAssets(borrowerId));
  const totalAssets = useSelector(selectFinancialInfoTotalAssets({ borrowerId }));
  const savedLiabilities = useSelector(
    selectFinancialInfoCurrentFinancialsBorrowerLiabilities(borrowerId),
  );
  const pendingChanges = useSelector(selectFinancialInfoPendingChanges);
  const liabilitiesPendingChanges =
    pendingChanges[FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES]];
  const totalLiabilities = useSelector(
    selectFinancialInfoTotalLiabilities({ borrowerId, pendingChanges: liabilitiesPendingChanges }),
  );
  const savedNegativeRentalIncome = useSelector(
    selectFinancialInfoCurrentFinancialsBorrowerNegativeRentalIncome(borrowerId),
  );
  const totalNegativeRentalIncome = useSelector(
    selectFinancialInfoTotalNegativeRentalIncome({ borrowerId }),
  );
  const getModifiedFieldPendingChangeAuthors = useModifiedFieldPendingChangeAuthors();

  const isAllBorrowersTabVisible = borrowers.length > 1;
  const isIncomesChanged = savedIncomes !== totalIncomes;
  const isAssetsChanged = savedAssets !== totalAssets;
  const isLiabilitiesChanged = savedLiabilities !== totalLiabilities;
  const isNegativeRentalIncomeChanged = savedNegativeRentalIncome !== totalNegativeRentalIncome;

  const summaryInfo = getSummaryInfoConfig({
    totalIncomes,
    totalAssets,
    totalLiabilities,
    totalNegativeRentalIncome,
    summaryPendingFields,
    projectedReserves,
    cashToClose,
    dti,
    isPersonalized: borrowerId !== ALL_BORROWERS_PATH && isAllBorrowersTabVisible,
    isIncomesChanged,
    isAssetsChanged,
    isLiabilitiesChanged,
    isNegativeRentalIncomeChanged,
  });

  const getHref = useCallback(
    borrowerId =>
      generateFinancialInfoPath({
        applicationId,
        tab,
        tablePath,
        borrowerId,
        actionType,
        recordId,
      }),
    [applicationId, tab, tablePath, actionType, recordId],
  );

  useEffect(() => {
    if (!borrowers.length || (borrowerId && borrowers.find(({ id }) => id === borrowerId))) {
      return;
    }

    const borrowerIdToSet = isAllBorrowersTabVisible ? ALL_BORROWERS_PATH : borrowers[0]?.id;

    history.push(getHref(borrowerIdToSet));
  }, [borrowers, borrowerId, getHref, history, isAllBorrowersTabVisible]);

  return (
    <Box sx={wrapperStyles}>
      <Box sx={tabsWrapperStyles}>
        {isLoading || !borrowers.length ? (
          <Box sx={tabsSkeletonsWrapperStyles}>
            {Array.from({ length: TABS_SKELETONS_LENGTH }).map((_, index) => (
              <Skeleton height={33} key={index} variant="rounded" width={162} />
            ))}
          </Box>
        ) : (
          <Tabs
            TabScrollButtonProps={tabScrollButtonProps}
            scrollButtons="auto"
            value={borrowerId ?? ALL_BORROWERS_PATH}
            variant="scrollable"
          >
            {isAllBorrowersTabVisible && (
              <NavigationTab
                icon={<UsersAvatarGroup usersData={borrowers} />}
                isModified={false}
                isSelected={borrowerId === ALL_BORROWERS_PATH}
                label={ALL_BORROWERS_LABEL}
                to={getHref(ALL_BORROWERS_PATH)}
                value={ALL_BORROWERS_PATH}
              />
            )}
            {borrowers.map(
              ({ fullName, id, firstName, lastName, modifiedFields, modifiedFieldDetails }) => (
                <NavigationTab
                  icon={<Avatar initials={getPersonInitials({ firstName, lastName })} />}
                  isModified={modifiedFields.includes(BORROWER_FULL_NAME_FIELD)}
                  isSelected={id === borrowerId}
                  key={id}
                  label={fullName}
                  pendingChangeAuthors={getModifiedFieldPendingChangeAuthors(
                    modifiedFieldDetails,
                    BORROWER_FULL_NAME_FIELD,
                  )}
                  to={getHref(id)}
                  value={id}
                />
              ),
            )}
          </Tabs>
        )}
      </Box>

      <Box
        sx={getSummaryWrapperStyles(isLoading ? SUMMARY_SKELETONS_LENGTH : summaryInfo.length)}
        onMouseLeave={onResetTooltipState}
        onMouseOver={onMouseOver}
      >
        <TruncatedProvider />

        {isLoading
          ? Array.from({ length: SUMMARY_SKELETONS_LENGTH }).map((_, index) => (
              <Box key={index} sx={summaryCardStyles}>
                <DataDisplay isLoading title="" value="" variant={dataDisplayVariant} />
              </Box>
            ))
          : summaryInfo.map(({ name, value, icon, iconColor, changeKind }) => (
              <Box key={name} sx={summaryCardStyles}>
                <DataDisplay
                  changeKind={changeKind}
                  icon={icon}
                  iconColor={iconColor}
                  title={name}
                  value={value}
                  variant={dataDisplayVariant}
                />
              </Box>
            ))}
      </Box>
    </Box>
  );
};

FinancialInfoHead.propTypes = {
  ...WithLoadingPropTypes,
};

export default FinancialInfoHead;
